import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { Input, Select } from '@shared/ui';
import {
  PROVIDER_FIELD_LIMITS,
  PROVIDER_TITLE_OTHER,
  joinProviderTitle,
  splitProviderTitle,
} from '../model/constants';
import { useProviderTitles } from '../model/useProviderTitles';

/**
 * The provider's credential: the standard list the API publishes, plus
 * an Other entry for anything else. Replaces the free-text box the four
 * provider forms each had, so a credential is picked rather than typed
 * — the patient SMS now appends it to the name ("John Smith, MD"), and
 * a typo used to reach the patient verbatim.
 *
 * The list comes from GET /accounts/{uuid}/providers/titles rather than
 * a constant, so a credential added on the backend appears without a
 * dashboard release. Order is the server's; Other is appended last.
 *
 * Leaving Other empty stores an empty title, which the API accepts: the
 * SMS then reads "John Smith at Acme" with no suffix. That is the only
 * way to say "this provider has no credential", so a form that requires
 * a title should say so through its own validation rather than by
 * preventing the choice here.
 *
 * `onChange` receives `{ target: { name, value } }`, the same shape the
 * plain `<Input>` it replaces produced — every call site keeps its
 * existing handler, whether that is Formik's `handleChange` or a local
 * setState.
 *
 * The stored value stays a single string: `splitProviderTitle` maps it
 * onto the two controls on the way in, `joinProviderTitle` on the way
 * out. Nothing else in the forms has to know the field has two halves.
 *
 * No `error` prop on purpose. Every call site already wraps this in a
 * `GField` that renders the message, exactly as it did around the old
 * `<Input>`; passing the string down as well would print it twice.
 */
const ProviderTitleField = ({
  value = '',
  onChange,
  name = 'providerTitle',
  id = 'providerTitle',
  disabled = false,
}) => {
  const { forms = {} } = useSelector(state => state.i18n) || {};
  const { accountUuid: paramAccountUuid } = useParams();
  const currentAccountUuid = useSelector(state => state.user?.metadata?.currentAccount?.uuid);
  // The route's account when inside one (a sponsor browsing a child),
  // else the user's own. The list is identical either way; the path
  // segment only carries the permission.
  const titles = useProviderTitles(paramAccountUuid || currentAccountUuid);

  const { option: derivedOption, custom } = useMemo(
    () => splitProviderTitle(value, titles),
    [value, titles]
  );

  // Picking Other has to survive an empty text box, and the stored value
  // cannot carry that: "Other with nothing typed" and "no title" are the
  // same empty string by design — that is what lets a provider have no
  // credential. So the choice is remembered here.
  //
  // The flag only speaks when the stored value is empty; any actual
  // value decides for itself. That keeps the two in step without an
  // effect — one syncing the flag to the value would immediately undo
  // the user's pick, because picking Other stores an empty string.
  const [otherPicked, setOtherPicked] = useState(() => derivedOption === PROVIDER_TITLE_OTHER);

  const option = derivedOption || (otherPicked ? PROVIDER_TITLE_OTHER : '');

  const options = useMemo(
    () => [
      ...titles.map(title => ({ value: title, label: title })),
      { value: PROVIDER_TITLE_OTHER, label: forms.providerTitleOther || 'Other' },
    ],
    [titles, forms.providerTitleOther]
  );

  const emit = next => onChange?.({ target: { name, value: next } });

  const handleOptionChange = event => {
    const nextOption = event?.target ? event.target.value : event;
    setOtherPicked(nextOption === PROVIDER_TITLE_OTHER);
    // Switching away from Other drops the typed text; switching INTO it
    // starts empty, which stores "no title" until something is typed.
    emit(joinProviderTitle(nextOption, nextOption === PROVIDER_TITLE_OTHER ? custom : ''));
  };

  const handleCustomChange = event => {
    emit(joinProviderTitle(PROVIDER_TITLE_OTHER, event.target.value));
  };

  return (
    <>
      <Select
        id={id}
        name={name}
        fullWidth
        value={option}
        options={options}
        onChange={handleOptionChange}
        placeholder={forms.providerTitlePlaceholder || 'Select a title'}
        disabled={disabled}
      />
      {option === PROVIDER_TITLE_OTHER && (
        <Input
          id={`${id}-other`}
          name={`${name}Other`}
          fullWidth
          style={{ marginTop: 8 }}
          value={custom}
          onChange={handleCustomChange}
          placeholder={forms.providerTitleOtherPlaceholder || 'e.g. NP-C'}
          maxLength={PROVIDER_FIELD_LIMITS.titleMax}
          disabled={disabled}
        />
      )}
    </>
  );
};

export default ProviderTitleField;
